import type {
  Enseigne,
  Client,
  CreateEnseigneRequest,
  CreateClientRequest,
  UpdateClientRequest,
} from '../types/preferences';

const API_BASE_URL = process.env.REACT_APP_FIDONE_API_URL || '/fidone-api';

class FidoneConfigService {
  private async callApi<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        const error: any = new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
        error.status = response.status;
        error.statusText = response.statusText;
        throw error;
      }

      return await response.json();
    } catch (error: any) {
      if (error.status !== 404) {
        console.error(`[API] Error [${options.method || 'GET'} ${endpoint}]:`, error);
      }
      throw error;
    }
  }

  async listEnseignes(): Promise<Enseigne[]> {
    return this.callApi<Enseigne[]>('/v1/enseignes', { method: 'GET' });
  }

  async getEnseigne(code: string): Promise<Enseigne> {
    return this.callApi<Enseigne>(`/v1/enseignes/${code}`, { method: 'GET' });
  }

  async createEnseigne(data: CreateEnseigneRequest): Promise<Enseigne> {
    return this.callApi<Enseigne>('/v1/enseignes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getClient(enseigneCode: string, clientId: string): Promise<Client> {
    return this.callApi<Client>(`/v1/enseignes/${enseigneCode}/clients/${clientId}`, {
      method: 'GET',
    });
  }

  async listClients(enseigneCode: string): Promise<Client[]> {
    return this.callApi<Client[]>(`/v1/enseignes/${enseigneCode}/clients`, {
      method: 'GET',
    });
  }

  async createClient(enseigneCode: string, data: CreateClientRequest): Promise<Client> {
    return this.callApi<Client>(`/v1/enseignes/${enseigneCode}/clients`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateClient(
    enseigneCode: string,
    clientId: string,
    data: UpdateClientRequest
  ): Promise<Client> {
    return this.callApi<Client>(`/v1/enseignes/${enseigneCode}/clients/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteClient(enseigneCode: string, clientId: string): Promise<void> {
    return this.callApi<void>(`/v1/enseignes/${enseigneCode}/clients/${clientId}`, {
      method: 'DELETE',
    });
  }
}

export const fidoneConfigService = new FidoneConfigService();
